import { useCallback, useState } from "react";
import { trainIdentifierIsEqual } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import { AffectedTrainsResponse } from "features/CenterContent/RoleContent/Vaktleder/types";
import { useWebsocket } from "websocket/useWebsocket";
import {
  BackendIdentifier,
  TrainInformationMessage,
  WebsocketTopic,
} from "websocket/websocketSharedWorkerTypes";

type UseAffectedTrainsWebsocketArgs = {
  isSuccess: boolean;
  affectedTrainsData?: AffectedTrainsResponse;
  refetch: () => Promise<unknown>;
};

export const useAffectedTrainsWebsocket = ({
  isSuccess,
  affectedTrainsData,
  refetch,
}: UseAffectedTrainsWebsocketArgs) => {
  /**
   * Refetch affected trains whenever we receive a new event for one of the affected trains.
   * If we receive a lot of messages at once, we only refetch after the last one.
   */
  const [, setTimeoutId] = useState<number | null>(null);

  const messageHandler = useCallback(
    ({ message }: TrainInformationMessage) => {
      const newTimeoutId = window.setTimeout(() => {
        // Half a second passed since last message, handle it
        setTimeoutId(null);

        if (isSuccess && affectedTrainsData) {
          const allAffectedTrains = [
            ...affectedTrainsData.affectedTrains,
            ...affectedTrainsData.handledAffectedTrains,
          ];

          const messageContainsAffectedTrain = message.versions.some(
            (version) =>
              allAffectedTrains.some((affectedTrain) =>
                trainIdentifierIsEqual(
                  affectedTrain.trainId,
                  version.trainIdentifier,
                ),
              ),
          );

          if (messageContainsAffectedTrain) {
            void refetch();
          }
        }
      }, 500);

      // Store the above timeoutId, clear any old unfinished timeouts
      setTimeoutId((prevTimeoutId) => {
        if (prevTimeoutId) {
          window.clearTimeout(prevTimeoutId);
        }
        return newTimeoutId;
      });
    },
    [isSuccess, affectedTrainsData, refetch],
  );

  useWebsocket(BackendIdentifier.DROPS_BACKEND, {
    [WebsocketTopic.TRAIN_INFORMATION]: messageHandler,
  });
};
